import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-cycle
import { useMD } from './md';

/**
 * useListerActions
 * @param backendURL {string}
 * @param viewType {string}
 * @param backPermissions {{
 *   canNew: boolean,
 *   canNewFolder: boolean,
 *   canCopy: boolean,
 *   canEdit: boolean,
 *   canDelete: boolean,
 *   canExecute: boolean,
 * }}
 * @param items [{
 *   id: string,
 *   repr: string.
 * }]
 * @param refedSelectedRows {{ current: string[] }}
 * @param openedItems string[]
 * @param setOpenedItems function(string[]),
 * @param onExecuteItems function(string[], string[]),
 * @param onDeleteItems function(string[], string[]),
 * @param onUnexecuteItems function(string[], string[]),
 * @param setShowDeleted function(boolean),
 * @param onUnloadChildren function(string),
 * @param onLoadChildren function(string),
 * @param setSearch function(string),
 * @param debouncedBackendSearch function(string),
 * @param setPeriod function,
 * @param onSetSettings function,
 * @param onChoice function,
 * @param setLocalFilter function,
 * @param onApproveItems function,
 * @param onReload function,
 * @param defaults {{}} - Значения по умолчанию для нового элемента,
 * @param clearMessages function,
 * @param setFilterOpened functon,
 * @returns {{
 *  onToggle: function,
 *  onSetFilter: function,
 *  onRefresh: function,
 *  onExecute: function,
 *  onSearch: function,
 *  onToggleShowDeleted: function,
 *  onNew: function,
 *  onEdit: function,
 *  onDelete: function,
 *  onUnexecute: function,
 *  onSetPeriod: function,
 *  onNewFolder: function,
 *  onRowDoubleClick: function,
 *  onApproveItems: function,
 *  onUnApproveItems: function,
 *  onApproveStatus: function,
 *  onApproveHistory: function,
 *  onCloseApproveStatus: function,
 *  onClearMessages: function,
 *  onDeleteMessage: function,
 *  onToggleFilterOpened: function,
 *  }}
 */
const useListerActions = ({
  backendURL,
  // eslint-disable-next-line no-unused-vars
  viewType,
  backPermissions,
  items,
  refedSelectedRows,
  openedItems,
  setOpenedItems,
  onExecuteItems,
  onDeleteItems,
  onUnexecuteItems,
  setShowDeleted,
  onUnloadChildren,
  onLoadChildren,
  setSearch,
  debouncedBackendSearch,
  setPeriod,
  onSetSettings,
  onChoice,
  setLocalFilter,
  onReload,
  clearMessages,
  deleteMessage,
  setFilterOpened,
  onExportXLS,
}) => {
  const md = useMD(backendURL);

  const history = useHistory();

  const onNew = useCallback(
    () => {
      if (!backPermissions.canNew) return null;
      return history.push(`${md.frontend}create`);
    },
    [backPermissions.canNew, history, md.frontend],
  );
  const onNewFolder = useCallback(
    () => backPermissions.canNewFolder && history.push(`${md.frontend}createGroup`),
    [backPermissions.canNewFolder, history, md.frontend],
  );
  const onEdit = useCallback(
    () => backPermissions.canEdit && refedSelectedRows.current.forEach((rowId) => history.push(`${md.frontend}${rowId}`)),
    [backPermissions.canEdit, history, md.frontend, refedSelectedRows],
  );
  const onDelete = useCallback(
    () => {
      if (backPermissions.canDelete) {
        const reprs = items
          .filter((item) => refedSelectedRows.current.includes(item.id))
          .reduce((R, row) => ({ ...R, [row.id]: row.repr }), {});
        onDeleteItems(refedSelectedRows.current, reprs);
      }
    },
    [backPermissions.canDelete, items, onDeleteItems, refedSelectedRows],
  );
  const onExecute = useCallback(
    () => {
      if (backPermissions.canExecute) {
        const reprs = items
          .filter((item) => refedSelectedRows.current.includes(item.id))
          .reduce((R, row) => ({ ...R, [row.id]: row.repr }), {});
        onExecuteItems(refedSelectedRows.current, reprs);
      }
    },
    [backPermissions.canExecute, items, onExecuteItems, refedSelectedRows],
  );

  const onUnexecute = useCallback(
    () => {
      if (backPermissions.canExecute) {
        const reprs = items
          .filter((item) => refedSelectedRows.current.includes(item.id))
          .reduce((R, row) => ({ ...R, [row.id]: row.repr }), {});
        onUnexecuteItems(refedSelectedRows.current, reprs);
      }
    },
    [backPermissions.canExecute, items, onUnexecuteItems, refedSelectedRows],
  );
  const onToggleShowDeleted = useCallback(
    () => setShowDeleted((v) => !v),
    [setShowDeleted],
  );

  const onRefresh = useCallback(
    () => onReload(),
    [onReload],
  );

  const onSearch = useCallback(
    (e, v) => {
      setSearch(v);
      debouncedBackendSearch(v);
    },
    [debouncedBackendSearch, setSearch],
  );
  const onSetPeriod = useCallback(
    (e, v) => {
      setPeriod(v);
      onSetSettings({
        period: v,
      });
    },
    [onSetSettings, setPeriod],
  );

  const onToggle = useCallback(
    (e, rowId) => {
      if (openedItems.includes(rowId)) {
        // Закрыли элемент
        const newOpenedItems = openedItems.filter((id) => id !== rowId);
        setOpenedItems(newOpenedItems);
        if (md.useHierarchyPagination) {
          onUnloadChildren(rowId);
        }
      } else {
        setOpenedItems([...openedItems, rowId]);
        if (md.useHierarchyPagination) {
          onLoadChildren(rowId);
        }
      }
    },
    [md.useHierarchyPagination, onLoadChildren, onUnloadChildren, openedItems, setOpenedItems],
  );

  const onRowDoubleClick = useCallback(
    (e, rowId) => {
      e.stopPropagation();
      // e.preventDefault();
      if (onChoice) {
        const selectedItem = items
          .filter((item) => item.id === rowId)
          .reduce((R, row) => row, null);
        onChoice(
          e,
          selectedItem,
        );
      } else {
        onEdit();
      }
    },
    [items, onChoice, onEdit],
  );

  const onSetFilter = useCallback(
    (name, value, use) => setLocalFilter((old) => ({
      ...old,
      [name]: { value, use },
    })),
    [setLocalFilter],
  );

  const onToggleFilterOpened = useCallback(
    () => setFilterOpened((o) => !o),
    [setFilterOpened],
  );

  const exportXLS = useCallback(
    () => {
      if (backPermissions.canExportXLS) onExportXLS();
    },
    [backPermissions.canExportXLS, onExportXLS],
  );
  const actions = useMemo(
    () => ({
      onNew,
      onNewFolder,
      onEdit,
      onDelete,
      onExecute,
      onUnexecute,
      onToggleShowDeleted,
      onSearch,
      onSetPeriod,
      onToggle,
      onRowDoubleClick,
      onSetFilter,
      onRefresh,
      onClearMessages: clearMessages,
      onDeleteMessage: deleteMessage,
      onToggleFilterOpened,
      onExportXLS: exportXLS,
    }),
    [onNew, onNewFolder, onEdit, onDelete, onExecute, onUnexecute, onToggleShowDeleted, onSearch, onSetPeriod, onToggle, onRowDoubleClick, onSetFilter, onRefresh, clearMessages, deleteMessage, onToggleFilterOpened, exportXLS],
  );

  return actions;
  // console.log(items);
};

export default useListerActions;
